import { getFlexiblePrecision, getFlexibleRange, getTaskEnd, isStartSchedule } from './scheduleUtils';
import { PlannerTaskInfo } from './types';

export type ScheduleBucketKey = 'overdue' | 'today' | 'thisWeek' | 'nextWeek' | 'thisMonth' | 'nextMonth' | 'thisYear' | 'future';
export type ScheduleBuckets = Record<ScheduleBucketKey, PlannerTaskInfo[]>;

const getTaskRange = (taskInfo: PlannerTaskInfo) => {
    const precision = getFlexiblePrecision(taskInfo.task);
    if (precision) {
        const { start, end } = getFlexibleRange(taskInfo.task);
        const inclusiveEnd = new Date(end);
        if (precision === 'day') inclusiveEnd.setHours(23, 59, 59, 999);
        if (precision === 'month') inclusiveEnd.setMonth(inclusiveEnd.getMonth() + 1, 0);
        if (precision === 'year') inclusiveEnd.setFullYear(inclusiveEnd.getFullYear(), 11, 31);
        inclusiveEnd.setHours(23, 59, 59, 999);
        return { start, end: inclusiveEnd };
    }
    const date = new Date(taskInfo.task.dueDate!);
    return { start: date, end: getTaskEnd(taskInfo.task) };
};

const sortTasks = (tasks: PlannerTaskInfo[]) => tasks.sort((first, second) => {
    if (first.task.completed !== second.task.completed) return first.task.completed ? 1 : -1;
    return getTaskRange(first).start.getTime() - getTaskRange(second).start.getTime();
});

export const getScheduleBuckets = (tasks: PlannerTaskInfo[], today: Date): ScheduleBuckets => {
    const buckets: ScheduleBuckets = {
        overdue: [], today: [], thisWeek: [], nextWeek: [], thisMonth: [], nextMonth: [], thisYear: [], future: [],
    };
    const startOfToday = new Date(today);
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date(startOfToday);
    endOfToday.setHours(23, 59, 59, 999);
    const endOfWeek = new Date(endOfToday);
    const daysSinceMonday = (startOfToday.getDay() + 6) % 7;
    endOfWeek.setDate(endOfWeek.getDate() + 6 - daysSinceMonday);
    const endOfNextWeek = new Date(endOfWeek);
    endOfNextWeek.setDate(endOfNextWeek.getDate() + 7);
    const nextMonth = new Date(startOfToday.getFullYear(), startOfToday.getMonth() + 1, 1);
    const endOfNextMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0, 23, 59, 59, 999);
    const uniqueTasks = Array.from(new Map(tasks.map((taskInfo) => [taskInfo.task.id, taskInfo])).values());

    uniqueTasks.forEach((taskInfo) => {
        const { start, end } = getTaskRange(taskInfo);
        if (taskInfo.task.completed && end < startOfToday) return;
        if (!taskInfo.task.completed && isStartSchedule(taskInfo.task.schedule) && start <= endOfToday) {
            buckets.today.push(taskInfo);
            return;
        }
        if (!taskInfo.task.completed && end < today) buckets.overdue.push(taskInfo);
        else if (start <= endOfToday && end >= startOfToday) buckets.today.push(taskInfo);
        else if (start <= endOfWeek) buckets.thisWeek.push(taskInfo);
        else if (start <= endOfNextWeek) buckets.nextWeek.push(taskInfo);
        else if (start.getFullYear() === today.getFullYear() && start.getMonth() === today.getMonth()) {
            buckets.thisMonth.push(taskInfo);
        } else if (start >= nextMonth && start <= endOfNextMonth) {
            buckets.nextMonth.push(taskInfo);
        } else if (start.getFullYear() === today.getFullYear()) buckets.thisYear.push(taskInfo);
        else buckets.future.push(taskInfo);
    });

    Object.values(buckets).forEach(sortTasks);
    return buckets;
};
